import { addressSchema } from "@/components/Forms/schema/addAddressSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";

const resourceSelector = createResourceCollectionSelector({
    type: "address",
    attributesSchema: addressSchema,
});

const addressSelector = createDataSelector(resourceSelector);

export type Addresses = ReturnType<typeof addressSelector>;
export type MrtAddress = Addresses[number];

type Props = {
    personUUID: string;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
};

export const useFetchMrtAddressesForPerson = ({
    personUUID,
    columnFilters,
    globalFilter,
    pagination,
    sorting,
}: Props): UseQueryResult<Addresses> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: [
            "person-addresses-table-data",
            personUUID,
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
        ],
        queryFn: async () => {
            const fetchURL = apiUrl("/person/address/for-person");

            const response = await fetch(fetchURL.href, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    personUUID: personUUID,
                    start: pagination.pageIndex * pagination.pageSize,
                    size: pagination.pageSize,
                    filters: columnFilters ?? [],
                    globalFilter: globalFilter ?? "",
                    sorting: sorting ?? [],
                }),
            });

            return await response.json();
        },
        select: addressSelector,
    });
};
